import { PATHS } from '@dadohr/shared/domainTypes/routes';
import queryString from 'query-string';
import { RouteComponentProps } from 'react-router';
import { PageExperienceDetails } from './features/Experiences/pages/Details';
import { PageExperiencesOverview } from './features/Experiences/pages/Overview';
import { ExperiencePreview } from './features/Experiences/pages/Preview';
import { PageExperienceTemplateCreate } from './features/Experiences/pages/TemplateCreate';
import { PageExperienceTemplateEdit } from './features/Experiences/pages/TemplateEdit';
import { PageHomeOverview } from './features/Home/pages/Overview';
import { PageMomentDetails } from './features/Moments/pages/Details';
import { PageMomentsOverview } from './features/Moments/pages/Overview';
import {
  PageOAuthRedirect,
  PageOAuthRedirectV2
} from './features/OAuth/pages/Redirect';
import { PageOrgAdminUsers } from './features/Org/pages/AdminUsers';
import { PageOrgAIFeatures } from './features/Org/pages/AIFeatures';
import { PageOrgBranding } from './features/Org/pages/Branding';
import { PageOrgUsersCustomFields } from './features/Org/pages/CustomFields';
import { PageOrgIntegrations } from './features/Org/pages/Integrations';
import { AIRTABLE_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/airtable';
import { ASANA_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/asana';
import { ATLASSIAN_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/atlassian';
import { CALENDLY_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/calendly';
import { DOCUSIGN_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/docusign';
import { GOOGLE_SHEETS_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/googleSheets';
import { PageGreenhouseV3OAuthRedirect } from './features/Org/pages/Integrations/integrations/greenhouseV3/PageOAuthRedirect';
import { MICROSOFT_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/microsoft';
import { PageSlackOAuthRedirect } from './features/Org/pages/Integrations/integrations/slack/PageOAuthRedirect';
import { SWAG_UP_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/swagUp';
import { SALESFORCE_OAUTH_REDIRECT_PROPS } from './features/Org/pages/Integrations/integrations/salesforce';
import { PageOrgSubscription } from './features/Org/pages/Subscription';
import { PageReportsDrilldown } from './features/Reports/pages/Drilldown';
import { PageReportsOverview } from './features/Reports/pages/Overview';
import { PageReportsTasks } from './features/Reports/pages/Tasks';
import { UpdateOrganizationInfo } from './features/SignUp/Pages/UpdateOrganizationInfo';
import { PageOrgUserImportsDetails } from './features/Users/pages/Imports/Details';
import { PageOrgUserImportsOverview } from './features/Users/pages/Imports/Overview';
import { PageUserImportSources } from './features/Users/pages/Imports/Sources';
import { PageOrgUsersOverview } from './features/Users/pages/Overview';
import { PageUserMerge, PageUserProfile } from './features/Users/pages/Profile';
import { PageUserSavedView } from './features/Users/pages/SavedView';
import { PageOrgUsersStandaloneOverview } from './features/Users/pages/Standaolne';
import { PageTargetAudiencesList } from './features/Users/pages/TargetAudiences/List';
import { PageTargetAudienceBuilder } from './features/Users/pages/TargetAudiences/Builder';
import { TFunction } from './i18next';

export interface IPage<T = any> {
  path: string;
  redirectFrom?: string[];
  render: (props: RouteComponentProps<T>) => JSX.Element;
  titleFragment: (t: TFunction) => string;
  trackingName: string;
}

// eslint-disable-next-line
const getQueryParam = <T extends {}>(
  search: string,
  key: string,
  defaultValue: T
) => {
  const query = queryString.parse(search);
  if (!query) {
    return defaultValue;
  }
  const value = query[key];
  if (value === undefined || value === null) {
    return defaultValue;
  }
  return (value as unknown) as T;
};

export const ExperiencesPreviewPage: IPage = {
  path: PATHS.experiencesPreview,
  render: (p: RouteComponentProps<{ previewToken: string }>) => (
    <ExperiencePreview previewToken={p.match.params.previewToken} />
  ),
  titleFragment: (t) => 'Experience Preview',
  trackingName: 'Experience Preview'
};

export const PAGES: IPage[] = [
  {
    path: PATHS.home.overview,
    render: () => <PageHomeOverview />,
    titleFragment: (t) => t('pageTitle.home'),
    trackingName: 'Home'
  },

  {
    path: PATHS.experiences.overview,
    render: () => <PageExperiencesOverview />,
    titleFragment: (t) => t('pageTitle.experiencesOverview'),
    trackingName: 'Experiences - List'
  },
  {
    path: PATHS.experiences.details.general,
    render: (p) => <PageExperienceDetails tplId={p.match.params.expId} />,
    titleFragment: (t) => t('pageTitle.experiencesDetails'),
    trackingName: 'Experiences - View'
  },
  {
    path: PATHS.experiences.details.edit,
    render: (p) => <PageExperienceTemplateEdit tplId={p.match.params.expId} />,
    titleFragment: (t) => t('pageTitle.experienceEdit'),
    trackingName: 'Experiences - Edit'
  },
  {
    path: PATHS.experiences.details.create,
    render: () => <PageExperienceTemplateCreate />,
    titleFragment: (t) => t('pageTitle.experienceCreate'),
    trackingName: 'Experiences - Create'
  },
  {
    path: PATHS.moments.overview,
    render: () => <PageMomentsOverview />,
    titleFragment: (t) => 'Moments', // TODO
    trackingName: 'Moments - List'
  },
  {
    path: PATHS.moments.details,
    render: (p) => (
      <PageMomentDetails momentTemplateId={p.match.params.momentTemplateId} />
    ),
    titleFragment: (t) => 'Moments', // TODO
    trackingName: 'Moments - Details'
  },

  {
    path: PATHS.reports.overview,
    render: () => <PageReportsOverview />,
    titleFragment: (t) => t('pageTitle.reportsOverview'),
    trackingName: 'Reporting - Overview'
  },

  {
    path: PATHS.reports.tasks,
    render: () => <PageReportsTasks />,
    titleFragment: (t) => t('pageTitle.reportsTasks'),
    trackingName: 'Reporting - Tasks'
  },
  {
    path: PATHS.reports.drilldown,
    render: () => <PageReportsDrilldown />,
    titleFragment: (t) => t('pageTitle.reportsDrilldown'),
    trackingName: 'Reporting - Completion Drill-down'
  },

  {
    path: PATHS.org.branding,
    render: () => <PageOrgBranding />,
    titleFragment: (t) => t('pageTitle.orgBranding'),
    trackingName: 'Organization - General'
  },
  {
    path: PATHS.org.subscription,
    render: () => <PageOrgSubscription />,
    titleFragment: (t) => t('pageTitle.orgSubscription'),
    trackingName: 'Organization - Subscription'
  },
  {
    path: PATHS.org.integrations,
    render: () => <PageOrgIntegrations />,
    titleFragment: (t) => t('pageTitle.orgIntegrations'),
    trackingName: 'Organization - Integrations'
  },
  {
    path: PATHS.users.overview,
    render: () => <PageOrgUsersOverview />,
    titleFragment: (t) => t('pageTitle.orgUsersOverview'),
    trackingName: 'Users - List'
  },
  {
    path: PATHS.users.standalone,
    render: () => <PageOrgUsersStandaloneOverview />,
    titleFragment: (t) => t('pageTitle.employeeList'),
    trackingName: 'Employees - List'
  },
  {
    path: PATHS.users.views.detail,
    render: (p) => <PageUserSavedView key={p.match.params.viewId} viewId={p.match.params.viewId} />,
    titleFragment: (t) => t('pageTitle.orgUsersOverview'),
    trackingName: 'Users - Saved View'
  },
  {
    path: PATHS.users.targetAudiences.list,
    render: () => <PageTargetAudiencesList />,
    titleFragment: () => 'Target audiences',
    trackingName: 'Users - Target Audiences - List'
  },
  {
    path: PATHS.users.targetAudiences.new,
    render: () => <PageTargetAudienceBuilder audienceId={null} />,
    titleFragment: () => 'New audience',
    trackingName: 'Users - Target Audiences - New'
  },
  {
    path: PATHS.users.targetAudiences.edit,
    render: (p) => (
      <PageTargetAudienceBuilder
        key={p.match.params.audienceId}
        audienceId={p.match.params.audienceId}
      />
    ),
    titleFragment: () => 'Edit audience',
    trackingName: 'Users - Target Audiences - Edit'
  },
  {
    path: PATHS.users.profile,
    render: (p) => <PageUserProfile userId={p.match.params.userId} />,
    titleFragment: (t) => t('pageTitle.orgUsersProfile'),
    trackingName: 'Users - Profile'
  },
  {
    path: PATHS.users.merge,
    render: (p) => (
      <PageUserMerge
        userId={p.match.params.userId}
        otherUserId={p.match.params.otherUserId}
      />
    ),
    titleFragment: () => 'Merge employees',
    trackingName: 'Users - Merge'
  },
  {
    path: PATHS.users.imports.overview,
    render: () => <PageOrgUserImportsOverview />,
    titleFragment: (t) => t('pageTitle.orgUsersImportsOverview'),
    trackingName: 'Users - Imports - List'
  },
  {
    path: PATHS.users.imports.details,
    render: (p) => (
      <PageOrgUserImportsDetails importId={p.match.params.importId} />
    ),
    titleFragment: (t) => t('pageTitle.orgUsersImportsDetails'),
    trackingName: 'Users - Imports - View'
  },
  {
    path: PATHS.users.imports.importSources,
    render: (p) => <PageUserImportSources />,
    titleFragment: (t) => t('pageTitle.orgUsersImportSources'),
    trackingName: 'Users - Imports - Sources'
  },
  {
    path: PATHS.org.customFields,
    render: () => <PageOrgUsersCustomFields />,
    titleFragment: (t) => t('pageTitle.orgUsersCustomFields'),
    trackingName: 'Organization - Data Fields'
  },
  {
    path: PATHS.org.adminUsers,
    render: () => <PageOrgAdminUsers />,
    titleFragment: (t) => t('pageTitle.orgAdminUsers'),
    trackingName: 'Organization - Admins'
  },
  {
    path: PATHS.org.aiFeatures,
    render: () => <PageOrgAIFeatures />,
    titleFragment: (t) => t('pageTitle.orgAIFeatures'),
    trackingName: 'Organization - AI Features'
  },

  {
    path: PATHS.integrations.oauth.asana,
    render: () => <PageOAuthRedirect {...ASANA_OAUTH_REDIRECT_PROPS} />,
    titleFragment: (t) => t('pageTitle.oauthAsana'),
    trackingName: 'OAuth Redirect - Asana'
  },
  {
    path: PATHS.integrations.oauth.airtable,
    render: () => (
      <PageOAuthRedirectV2
        integrationName={AIRTABLE_OAUTH_REDIRECT_PROPS.integrationName}
      />
    ),
    titleFragment: (t) => t('pageTitle.oauthAirtable'),
    trackingName: 'OAuth Redirect - Airtable'
  },
  {
    path: PATHS.integrations.oauth.atlassian,
    render: () => <PageOAuthRedirect {...ATLASSIAN_OAUTH_REDIRECT_PROPS} />,
    titleFragment: (t) => t('pageTitle.oauthAtlassian'),
    trackingName: 'OAuth Redirect - Atlassian'
  },
  {
    path: PATHS.integrations.oauth.calendly,
    render: () => <PageOAuthRedirect {...CALENDLY_OAUTH_REDIRECT_PROPS} />,
    titleFragment: (t) => t('pageTitle.oauthCalendly'),
    trackingName: 'OAuth Redirect - Calendly'
  },
  {
    path: PATHS.integrations.oauth.docusign,
    render: () => (
      <PageOAuthRedirectV2
        integrationName={DOCUSIGN_OAUTH_REDIRECT_PROPS.integrationName}
      />
    ),
    titleFragment: (t) => t('pageTitle.oauthAirtable'),
    trackingName: 'OAuth Redirect - Docusign'
  },
  {
    path: PATHS.integrations.oauth.googleSheets,
    render: () => <PageOAuthRedirect {...GOOGLE_SHEETS_OAUTH_REDIRECT_PROPS} />,
    titleFragment: (t) => t('pageTitle.oauthGoogleSheets'),
    trackingName: 'OAuth Redirect - Google'
  },
  {
    path: PATHS.integrations.oauth.microsoft,
    render: () => <PageOAuthRedirect {...MICROSOFT_OAUTH_REDIRECT_PROPS} />,
    titleFragment: (t) => t('pageTitle.oauthMicrosoft'),
    trackingName: 'OAuth Redirect - Microsoft'
  },
  {
    path: PATHS.integrations.oauth.swagUp,
    render: () => <PageOAuthRedirect {...SWAG_UP_OAUTH_REDIRECT_PROPS} />,
    titleFragment: (t) => t('pageTitle.oauthSwagUp'),
    trackingName: 'OAuth Redirect - SwagUp'
  },
  {
    // SF OAuth callback. PATHS.integrations.oauth.salesforce will arrive in the
    // next @dadohr/shared bump; using the literal path keeps SF-11 unblocked
    // and exactly matches the redirectUri configured in env.json.
    path: '/integrations/salesforce/oauth',
    render: () => <PageOAuthRedirect {...SALESFORCE_OAUTH_REDIRECT_PROPS} />,
    titleFragment: (t) => t('pageTitle.oauthSalesforce'),
    trackingName: 'OAuth Redirect - Salesforce'
  },
  {
    path: PATHS.integrations.oauth.rippling,
    render: () => <PageOAuthRedirectV2 integrationName="Rippling" />,
    titleFragment: (t) => t('pageTitle.oauthRippling'),
    trackingName: 'OAuth Redirect - Rippling'
  },
  {
    // Greenhouse (v3) OAuth landing route. The backend redirects here after the
    // Greenhouse consent screen with `?orgId=` (success) or `?error=` (failure).
    // Literal path until PATHS.integrations.oauth.greenhouseV3 lands in the next
    // @dadohr/shared bump; must match the backend's configured redirect target.
    path: '/integrations/oauth/greenhouse-v3',
    render: () => <PageGreenhouseV3OAuthRedirect />,
    titleFragment: () => 'Greenhouse',
    trackingName: 'OAuth Redirect - Greenhouse (v3)'
  },
  {
    path: PATHS.integrations.oauth.slack,
    render: (p) => <PageSlackOAuthRedirect slackId={p.match.params.slackId} />,
    titleFragment: (t) => t('pageTitle.oautSlack'),
    trackingName: 'OAuth Redirect - Google'
  },
  {
    path: PATHS.signUp.updateOrgInfo,
    render: (p) => <UpdateOrganizationInfo />,
    titleFragment: (t) => 'Moment Sign up',
    trackingName: 'Update Free Tier'
  },
  ExperiencesPreviewPage
];

export interface INavCategory {
  id: string;
  route: string;
  icon: JSX.Element;
  disabled?: boolean;
  hidden?: boolean;
  isActive: () => boolean;
}

export interface INavCategoryGroup {
  id: string;
  children: INavCategory[];
}

// @ts-ignore
export const urlIncludes = (url: string | string[]) => {
  if (Array.isArray(url) && url.find((u) => window.location.href.includes(u))) {
    return true;
  }
  return window.location.href.includes(url as string);
};
